import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from "react-i18next";
import "../i18n"; // Import i18n setup

const ContactUs = () => {
  const { t } = useTranslation(['common', 'contactUs']);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('');
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    let formErrors = {};

    if (!formData.name.trim()) formErrors.name = t("contactUs:validationMsg.nameRequired");
    if (!formData.email) {
      formErrors.email = t("contactUs:validationMsg.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      formErrors.email = t("contactUs:validationMsg.emailRequired");
    }
    if (!formData.subject.trim()) formErrors.subject = t("contactUs:validationMsg.subjectRequired");
    if (!formData.message.trim()) formErrors.message = t("contactUs:validationMsg.messageRequired");

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(t("contactUs:msg.sending"));

    if (!validateForm()) {
      setStatus(t("contactUs:msg.fillCorrectly"));
      return;
    }

    try {
      const response = await axios.post(
        'https://rjm3o0gzdc.execute-api.us-east-1.amazonaws.com/prod/contact',
        formData,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      setStatus(response.data.message);
      setFormData({ name: '', email: '', subject: '', message: '' }); // Reset form
    } catch (error) {
      setStatus(t("contactUs:validationMsg.emailSendingFailure"));
    }
  };

  return (
    <section className="min-h-screen py-16 px-6 lg:px-20">
      <div className="max-w-7xl mx-auto">
        {/* Heading */}
        <h1 className="text-4xl lg:text-5xl font-bold text-center mb-12">{t("common:contactUs")}</h1>

        {/* Grid layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-gray-900 rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-semibold mb-6">{t("contactUs:sendUsMessage")}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t("contactUs:emailForm.name")}
                className="w-full px-4 py-3 rounded-lg bg-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
                required
              />
              {errors.name && <p className="text-red-500">{errors.name}</p>}

              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t("contactUs:emailForm.email")}
                className="w-full px-4 py-3 rounded-lg bg-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
                required
              />
              {errors.name && <p className="text-red-500">{errors.email}</p>}

              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder={t("contactUs:emailForm.subject")}
                className="w-full px-4 py-3 rounded-lg bg-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
                required
              />
              {errors.name && <p className="text-red-500">{errors.subject}</p>}

              <textarea
                rows="5"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder={t("contactUs:emailForm.message")}
                className="w-full px-4 py-3 rounded-lg bg-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
                required
              ></textarea>
              {errors.name && <p className="text-red-500">{errors.message}</p>}

              <button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-600 py-3 px-6 rounded-lg font-semibold text-white">
                {t("contactUs:emailForm.send")}
              </button>
              {status && <p className="mt-2 text-sm text-gray-300">{status}</p>}
            </form>
          </div>

          {/* Info and Map */}
          <div className="space-y-8">
            {/* Address */}
            <div>
              <h2 className="text-2xl font-semibold mb-4">{t("contactUs:headOffice")}</h2>
              <p className="text-gray-300">{t("common:address")}</p>
              <p className="text-gray-300 mt-2">{t("common:phone")}: {t("common:phoneNumber")}</p>
              <p className="text-gray-300">
                {t("common:email")}: {" "}
                <a
                  href={`mailto:${t("common:contactEmail")}`}
                  className="text-blue-600 hover:underline"
                >
                  {t("common:contactEmail")}
                </a>
              </p>
            </div>

            {/* Map */}
            <div className="overflow-hidden rounded-2xl shadow-lg">
              <iframe
                title="Google Map showing main office location"
                className="w-full h-64"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3230.168169004757!2d139.6712420115363!3d35.9428558723893!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6018c74b251e38c1%3A0x397d1ab78a40d2d5!2s3-ch%C5%8Dme-119%20Miyagayat%C5%8D%2C%20Minuma%20Ward%2C%20Saitama%2C%20337-0011!5e0!3m2!1sen!2sjp!4v1745335398037!5m2!1sen!2sjp"
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </div>
          </div>
        </div>

        {/* Worldwide Branches */}
        <div className="mt-20">
          <h2 className="text-3xl font-semibold text-center mb-8">{t("contactUs:globalRepresentatives")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                city: t("contactUs:gRUSA.city"),
                flag: '🇺🇸',
                address: t("contactUs:gRUSA.address"),
                phone: t("contactUs:gRUSA.phone"),
              },
              {
                city: t("contactUs:gRItaly.city"),
                flag: '🇮🇹',
                address: t("contactUs:gRItaly.address"),
                phone: t("contactUs:gRItaly.phone"),
              },
              {
                city: t("contactUs:gRSweden.city"),
                flag: '🇸🇪',
                address: t("contactUs:gRSweden.address"),
                phone: t("contactUs:gRSweden.phone"),
              },
              {
                city: t("contactUs:gRBangladesh.city"),
                flag: '🇧🇩',
                address: t("contactUs:gRBangladesh.address"),
                phone: t("contactUs:gRBangladesh.phone"),
              },
            ].map((branch, idx) => (
              <div
                key={idx}
                className="bg-gray-900 hover:bg-gray-800 rounded-xl p-6 shadow text-center space-y-3"
              >
                {/* Flag */}
                <div className="text-4xl">{branch.flag}</div>

                {/* City Name */}
                <h3 className="text-xl font-bold">{branch.city}</h3>

                {/* Address */}
                <p className="text-gray-400 text-sm">{branch.address}</p>

                {/* Phone */}
                <p className="text-gray-400 text-sm">Phone: {branch.phone}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default ContactUs;